import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHeader, Card, Button, Modal } from '../components/UI';
import api, { documentApi } from '../api';
import Select from '../components/Select';
import { useAuth } from '../context/AuthContext';
import * as I from 'lucide-react';
import { toast } from 'react-toastify';

const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = date.getDate();
        let daySuffix = 'th';
        if (day === 1 || day === 21 || day === 31) daySuffix = 'st';
        else if (day === 2 || day === 22) daySuffix = 'nd';
        else if (day === 3 || day === 23) daySuffix = 'rd';
        return `${day}${daySuffix} ${date.toLocaleDateString('en-IN', { month: 'short' })} ${date.getFullYear()}`;
    } catch (e) {
        console.warn('Invalid date format:', dateString);
        return 'Invalid Date';
    }
};

const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const datePart = `${day}/${month}/${year}`;

        const timePart = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        return `${datePart} ${timePart}`;
    } catch (e) {
        console.warn('Invalid datetime format:', dateString);
        return 'Invalid Date';
    }
};

const InvoiceDetailPage = ({
    invoice: initialInvoice,
    onBack = () => window.history.back()
}) => {
    const [invoice, setInvoice] = useState(initialInvoice);
    const [loading, setLoading] = useState(false);

    // Fetch fresh invoice details
    const fetchInvoiceDetails = useCallback(async () => {
        if (!initialInvoice?.id) return;
        setLoading(true);
        try {
            const response = await api.get(`/invoices/${initialInvoice.id}`);
            console.log('[InvoiceDetailPage] Fetched fresh invoice data:', response.data);
            setInvoice(response.data);
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            toast.error('Failed to update invoice details');
        } finally {
            setLoading(false);
        }
    }, [initialInvoice?.id]);

    // Fetch on mount
    useEffect(() => {
        fetchInvoiceDetails();
    }, [fetchInvoiceDetails]);

    const { isAuditor } = useAuth(); // AUDITOR is read-only: hide all edit controls
    const [dueDate, setDueDate] = useState(invoice?.dueDate || '');
    const [savingDueDate, setSavingDueDate] = useState(false);
    const [showPdf, setShowPdf] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [tdsBatchInfo, setTdsBatchInfo] = useState(null);
    const [sendingNotification, setSendingNotification] = useState(false);
    const [showEmailLogModal, setShowEmailLogModal] = useState(false);
    const [emailLogModalData, setEmailLogModalData] = useState({ subject: '', body: '' });

    // Update local state when invoice changes (e.g. after fetch)
    useEffect(() => {
        if (invoice) {
            setDueDate(invoice.dueDate || '');
        }
    }, [invoice]);

    // Edit amounts state
    const [isEditingAmounts, setIsEditingAmounts] = useState(false);
    const [editingAmounts, setEditingAmounts] = useState({
        subtotal: invoice?.subtotal || 0,
        tax: invoice?.tax || 0,
        tdsAmount: invoice?.tdsAmount || 0,
        invoicePayable: invoice?.invoicePayable || 0
    });
    const [savingAmounts, setSavingAmounts] = useState(false);

    // TDS Rules state
    const [tdsRules, setTdsRules] = useState([]);
    const [loadingTdsRules, setLoadingTdsRules] = useState(false);

    // TDS Edit modal state
    const [isTdsEditModalOpen, setIsTdsEditModalOpen] = useState(false);
    const [updatingTds, setUpdatingTds] = useState(false);

    // Form state for TDS update
    const [tdsFormData, setTdsFormData] = useState({
        tdsRuleId: '',
        tdsPercentage: 0
    });

    const handleViewHardCopy = async () => {
        console.log('[INVOICE-DETAIL] handleViewHardCopy called');
        console.log('[INVOICE-DETAIL] Invoice data:', invoice);

        // Check for S3 key in invoice (Now using documentUrl field)
        const documentUrlS3Key = invoice?.documentUrl;
        console.log('[INVOICE-DETAIL] Document URL S3 key:', documentUrlS3Key);

        if (!documentUrlS3Key) {
            console.log('[INVOICE-DETAIL] No document URL found, showing warning');
            toast.warning('No invoice document is available for this record.');
            return;
        }

        console.log('[INVOICE-DETAIL] Starting API call to get document URL');
        try {
            // Get pre-signed URL from backend
            console.log('[INVOICE-DETAIL] Fetching document URL for invoice ID:', invoice.id);
            const response = await documentApi.getInvoiceDocumentUrl(invoice.id);
            console.log('[INVOICE-DETAIL] API response:', response);
            const documentUrl = response.data; // Backend returns string directly
            console.log('[INVOICE-DETAIL] Extracted document URL:', documentUrl);

            if (!documentUrl) {
                console.log('[INVOICE-DETAIL] Empty document URL received, showing warning');
                toast.warning('No invoice document is available for this record.');
                return;
            }

            setPdfUrl(documentUrl);
            setShowPdf(true);
            console.log('[INVOICE-DETAIL] PDF URL set and modal shown');
        } catch (error) {
            console.error('[INVOICE-DETAIL] Error fetching document URL:', error);
            toast.error('Failed to fetch document URL. Please try again or contact support.');
        }
    };

    const handleViewChallan = async () => {
        // Check for challan S3 key in invoice
        const challanS3Key = invoice?.challanS3Key;

        if (!challanS3Key) {
            toast.warning('No TDS challan is available for this record.');
            return;
        }

        try {
            // Get pre-signed URL from backend
            console.log('[INVOICE-DETAIL] Fetching challan URL for invoice ID:', invoice.id);
            const response = await documentApi.getChallanDocumentUrl(invoice.id);
            console.log('[INVOICE-DETAIL] Challan API response:', response);
            const challanUrl = response.data; // Backend returns string directly
            console.log('[INVOICE-DETAIL] Extracted challan URL:', challanUrl);

            if (!challanUrl) {
                toast.warning('No TDS challan is available for this record.');
                return;
            }

            setPdfUrl(challanUrl);
            setShowPdf(true);
            console.log('[INVOICE-DETAIL] Challan PDF URL set and modal shown');
        } catch (error) {
            console.error('[INVOICE-DETAIL] Error fetching challan URL:', error);
            toast.error('Failed to fetch challan URL. Please try again or contact support.');
        }
    };

    const handleSendPaymentNotification = async () => {
        if (!invoice?.id) return;

        try {
            setSendingNotification(true);
            const response = await api.get(`/invoices/${invoice.id}/payment-notification-preview`);
            const preview = response.data;

            toast(
                <div className="flex flex-col space-y-3 p-2">
                    <div className="flex items-center">
                        <I.Mail className="w-5 h-5 text-blue-500 mr-2" />
                        <p className="text-sm font-medium text-gray-800">Preview Payment Notification</p>
                    </div>
                    <div className="text-xs text-gray-700 space-y-2 max-h-[500px] overflow-y-auto bg-gray-50 p-3 rounded border">
                        <p><strong>To:</strong> {preview.recipientEmail}</p>
                        {preview.ccEmails && preview.ccEmails.length > 0 && (
                            <p><strong>CC:</strong> {preview.ccEmails.join(', ')}</p>
                        )}
                        <p><strong>Subject:</strong> {preview.subject}</p>
                        <div className="border-t pt-2 mt-2">
                            <strong>Body:</strong>
                            <pre className="whitespace-pre-wrap font-sans mt-1 text-[12px] leading-relaxed">
                                {preview.body}
                            </pre>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                toast.dismiss();
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                    await api.post(`/invoices/${invoice.id}/send-payment-notification`);
                                    toast.dismiss();
                                    toast.success('Payment notification sent successfully');
                                } catch (error) {
                                    console.error('Error sending payment notification:', error);
                                    toast.error(error.message || 'Failed to send payment notification');
                                }
                            }}
                        >
                            Confirm & Send
                        </button>
                    </div>
                </div>,
                {
                    autoClose: false,
                    closeButton: false,
                    closeOnClick: false,
                    draggable: false,
                    position: 'top-center',
                    className: 'min-w-[600px] bg-white border border-gray-200 shadow-xl rounded-lg',
                    bodyClassName: 'p-0',
                    hideProgressBar: true,
                }
            );
        } catch (error) {
            console.error('Error fetching email preview:', error);
            toast.error('Failed to fetch email preview');
        } finally {
            setSendingNotification(false);
        }
    };

    useEffect(() => {
        const fetchTdsBatchInfo = async () => {
            if (!invoice?.tdsBatchId) {
                setTdsBatchInfo(null);
                return;
            }

            try {
                const response = await api.get(`/tds/batches/${invoice.tdsBatchId}`);
                setTdsBatchInfo(response.data || null);
            } catch (error) {
                console.error('Failed to fetch TDS batch info:', error);
                setTdsBatchInfo(null);
            }
        };

        fetchTdsBatchInfo();
    }, [invoice?.tdsBatchId]);

    const handleDueDateUpdate = () => {
        if (!invoice?.id) {
            toast.error('Cannot update due date: invoice ID is missing.');
            return;
        }

        if (!dueDate) {
            toast.warning('Please select a valid due date.');
            return;
        }

        // Show confirmation toast
        toast(
            <div className="flex flex-col space-y-3 p-2">
                <div className="flex items-center">
                    <I.Calendar className="w-5 h-5 text-blue-500 mr-2" />
                    <p className="text-sm font-medium text-gray-800">Update Due Date</p>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                    Are you sure you want to update the due date to {new Date(dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}?
                </p>
                <div className="flex justify-end space-x-3 mt-2">
                    <button
                        className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            toast.dismiss();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        onClick={async (e) => {
                            e.stopPropagation();
                            try {
                                setSavingDueDate(true);
                                const payload = { dueDate };
                                await api.put(`/invoices/${invoice.id}/due-date`, payload);
                                toast.dismiss();
                                toast.success('Due date updated successfully.', {
                                    position: 'top-center',
                                    autoClose: 3000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    className: 'text-center',
                                    bodyClassName: 'flex justify-center',
                                });
                            } catch (error) {
                                console.error('Failed to update due date:', error);
                                toast.error('Failed to update due date. Please try again.');
                            } finally {
                                setSavingDueDate(false);
                            }
                        }}
                        disabled={savingDueDate}
                    >
                        {savingDueDate ? 'Updating...' : 'Update Date'}
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                draggable: false,
                position: 'top-center',
                className: 'min-w-[320px] bg-white border border-gray-200 shadow-lg rounded-lg',
                bodyClassName: 'p-0',
                hideProgressBar: true,
            }
        );
    };

    const handleAmountEdit = () => {
        setIsEditingAmounts(true);
        setEditingAmounts({
            subtotal: invoice?.subtotal || 0,
            tax: invoice?.tax || 0,
            tdsAmount: invoice?.tdsAmount || 0,
            invoicePayable: invoice?.invoicePayable || 0
        });
    };

    const handleAmountChange = (field, value) => {
        const numValue = parseFloat(value) || 0;

        setEditingAmounts(prev => {
            const updated = { ...prev, [field]: numValue };

            // Recalculate dependent amounts when subtotal or tax changes
            if (field === 'subtotal' || field === 'tax') {
                const subtotal = updated.subtotal || 0;
                const tax = updated.tax || 0;
                const totalAmount = subtotal + tax;

                // Recalculate TDS amount based on current TDS percentage (calculated from subtotal, not total)
                const tdsPercentage = invoice?.tdsPercentage || 0;
                const tdsAmount = subtotal * (tdsPercentage / 100);

                // Recalculate invoice payable
                const invoicePayable = totalAmount - tdsAmount;

                updated.totalAmount = totalAmount;
                updated.tdsAmount = tdsAmount;
                updated.invoicePayable = invoicePayable;
            }

            return updated;
        });
    };

    const handleAmountUpdate = async () => {
        if (!invoice?.id) {
            toast.error('Cannot update amounts: invoice ID is missing.');
            return;
        }

        toast(
            <div className="flex flex-col space-y-3 p-2">
                <div className="flex items-center">
                    <I.DollarSign className="w-5 h-5 text-green-500 mr-2" />
                    <p className="text-sm font-medium text-gray-800">Update Invoice Amounts</p>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                    Are you sure you want to update the invoice amounts?
                </p>
                <div className="flex justify-end space-x-3 mt-2">
                    <button
                        className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            toast.dismiss();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-4 py-1.5 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        onClick={async (e) => {
                            e.stopPropagation();
                            try {
                                setSavingAmounts(true);
                                const payload = {
                                    subtotal: editingAmounts.subtotal,
                                    tax: editingAmounts.tax
                                };
                                const response = await api.put(`/invoices/${invoice.id}/amounts`, payload);

                                // Update local invoice data with backend response
                                Object.assign(invoice, response.data);

                                setIsEditingAmounts(false);
                                toast.dismiss();
                                toast.success('Invoice amounts updated successfully.', {
                                    position: 'top-center',
                                    autoClose: 3000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    className: 'text-center',
                                    bodyClassName: 'flex justify-center',
                                });
                            } catch (error) {
                                console.error('Failed to update amounts:', error);
                                toast.error('Failed to update amounts. Please try again.');
                            } finally {
                                setSavingAmounts(false);
                            }
                        }}
                        disabled={savingAmounts}
                    >
                        {savingAmounts ? 'Updating...' : 'Update'}
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                draggable: false,
                position: 'top-center',
                className: 'min-w-[320px] bg-white border border-gray-200 shadow-lg rounded-lg',
                bodyClassName: 'p-0',
                hideProgressBar: true,
            }
        );
    };

    const handleAmountCancel = () => {
        setIsEditingAmounts(false);
        setEditingAmounts({
            subtotal: invoice?.subtotal || 0,
            tax: invoice?.tax || 0,
            tdsAmount: invoice?.tdsAmount || 0,
            invoicePayable: invoice?.invoicePayable || 0
        });
    };

    // Fetch TDS Rules
    const fetchTdsRules = useCallback(async () => {
        setLoadingTdsRules(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }

            const response = await api.get('/tds-rules', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const rules = Array.isArray(response.data) ? response.data : [];
            setTdsRules(rules);
        } catch (error) {
            console.error('Error fetching TDS rules:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch TDS rules');
        } finally {
            setLoadingTdsRules(false);
        }
    }, []);

    // Fetch TDS rules on component mount
    useEffect(() => {
        fetchTdsRules();
    }, [fetchTdsRules]);

    // Get TDS rule description from ID
    const getTdsRuleDescription = useCallback((tdsRuleValue) => {
        if (!tdsRuleValue) return 'N/A';

        // Try to find by ID first (if rules are loaded)
        if (tdsRules.length > 0) {
            const rule = tdsRules.find(r => r.id.toString() === tdsRuleValue.toString());
            if (rule) {
                return rule.description;
            }
        }

        // If not found by ID, return the value as is (likely already a description)
        return tdsRuleValue;
    }, [tdsRules]);

    // TDS Rule Options for Select
    const tdsRuleOptions = useMemo(() => {
        if (loadingTdsRules) return [{ value: '', label: 'Loading TDS sections...' }];
        if (!tdsRules || tdsRules.length === 0) return [{ value: '', label: 'No TDS sections available' }];
        return [
            { value: '', label: 'Select TDS Section...' },
            ...tdsRules.map(rule => ({
                value: rule.id,
                label: rule.description,
                percentage: rule.percentage
            }))
        ];
    }, [tdsRules, loadingTdsRules]);

    // Handle TDS edit click
    const handleTdsEditClick = useCallback(() => {
        if (!invoice) return;

        // Set initial form data from the invoice object
        // Try to map description back to ID if possible
        let initialRuleId = invoice.tdsRule || invoice.tdsRuleId || '';
        if (initialRuleId && tdsRules.length > 0) {
            const ruleByDesc = tdsRules.find(r => r.description === initialRuleId);
            if (ruleByDesc) {
                initialRuleId = ruleByDesc.id;
            }
        }

        setTdsFormData({
            tdsRuleId: initialRuleId,
            tdsPercentage: invoice.tdsPercentage || 0
        });

        setIsTdsEditModalOpen(true);
    }, [invoice, tdsRules]);

    // Handle TDS update
    const handleTdsUpdate = async (e) => {
        e.preventDefault();
        if (!invoice || !tdsFormData.tdsRuleId) {
            toast.error('Please select a valid TDS tax section for this invoice');
            return;
        }

        setUpdatingTds(true);
        try {
            const token = localStorage.getItem('token');
            // Find the selected rule with loose comparison to handle string/number IDs
            const selectedRule = tdsRules.find(rule =>
                rule.id.toString() === tdsFormData.tdsRuleId.toString() ||
                rule.id === tdsFormData.tdsRuleId
            );

            if (!selectedRule) {
                toast.error('Selected TDS tax rule not found for this invoice');
                setUpdatingTds(false);
                return;
            }

            const tdsPercentage = parseFloat(selectedRule.percentage);
            const tdsRuleId = selectedRule.id;

            const response = await api.put(
                `/invoices/${invoice.id}/tds`,
                {
                    tdsRuleId: tdsRuleId,
                    tdsRuleDescription: selectedRule.description,
                    tdsPercentage: tdsPercentage
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                }
            );

            // Update the local invoice data with the response from backend
            setInvoice(response.data);

            // Update editing amounts if in edit mode
            setEditingAmounts(prev => ({
                ...prev,
                tdsAmount: response.data.tdsAmount || 0,
                invoicePayable: response.data.invoicePayable || 0
            }));

            toast.success('TDS tax rate updated successfully for invoice');
            setIsTdsEditModalOpen(false);
        } catch (error) {
            console.error('Error updating TDS:', {
                error: error,
                response: error.response?.data
            });
            toast.error(error.response?.data?.message || 'Failed to update TDS tax rate for invoice');
        } finally {
            setUpdatingTds(false);
        }
    };



    if (!invoice) {
        return (
            <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-screen">
                <PageHeader
                    title="Invoice Details"
                    subtitle="No invoice selected."
                    actions={[
                        <Button key="back" variant="outline" onClick={onBack}>
                            <I.ArrowLeft className="w-4 h-4 mr-1" /> Back to Invoice Management
                        </Button>
                    ]}
                />
                <Card className="p-6 text-gray-600 text-sm">
                    Please go back to Invoice Management and choose an invoice to view.
                </Card>
            </div>
        );
    }

    const vendorName = invoice.vendorName || `ID: ${invoice.vendorId ?? 'N/A'}`;

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
            <PageHeader
                title="Invoice Details"
                subtitle="Review the full details of the selected invoice"
                actions={[
                    <Button key="back" variant="outline" onClick={onBack}>
                        <I.ArrowLeft className="w-4 h-4 mr-1" /> Back to Invoice Management
                    </Button>,
                    <Button key="hardcopy" variant="outline" onClick={handleViewHardCopy}>
                        <I.FileText className="w-4 h-4 mr-1" /> View Document
                    </Button>,
                    invoice?.challanS3Key && (
                        <Button key="challan" variant="outline" onClick={handleViewChallan}>
                            <I.FileText className="w-4 h-4 mr-1" /> View Challan
                        </Button>
                    ),
                    !isAuditor && ['INVOICE_PAID', 'TDS_PENDING', 'TDS_PAID', 'COMPLETED'].includes(String(invoice?.status || '').toUpperCase()) && (
                        <Button key="send-notification" variant="outline" onClick={handleSendPaymentNotification}>
                            <I.Mail className="w-4 h-4 mr-1" /> Send Notification
                        </Button>
                    )
                ].filter(Boolean)}
            />

            <Card className="p-6 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-4">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Invoice Number</p>
                        <div className="flex items-center flex-wrap gap-3 mt-1">
                            <p className="text-xl font-semibold text-gray-900 break-words">
                                {invoice.invoiceNumber || invoice.id}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-start md:items-end gap-2">
                        <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                            <p className="text-lg font-semibold text-gray-900">{invoice.status || 'UNKNOWN'}</p>
                        </div>
                    </div>
                </div>

                {/* Row 1: Invoice details, laid out side by side */}
                <div className="grid grid-cols-1 gap-6">
                    <div>
                        <div className="rounded-lg border bg-white p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 text-sm">
                                <div>
                                    <p className="text-gray-500">Vendor</p>
                                    <p className="font-medium text-gray-900 break-words">{vendorName}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Invoice Date</p>
                                    <p className="font-medium text-gray-900">{formatDate(invoice.invoiceDate)}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Date Received</p>
                                    <p className="font-medium text-gray-900">{formatDate(invoice.dateReceived)}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Due Date</p>
                                    <div className="flex items-center gap-1 mt-1">
                                        <input
                                            type="date"
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                                            value={dueDate || ''}
                                            onChange={(e) => setDueDate(e.target.value)}
                                            disabled={isAuditor}
                                        />
                                        {!isAuditor && (
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                className="border border-gray-300 px-1.5 py-0 h-7 text-xs hover:bg-gray-100"
                                                onClick={handleDueDateUpdate}
                                                disabled={savingDueDate}
                                            >
                                                {savingDueDate ? 'Saving...' : 'Update'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2: TDS details and Invoice summary on the same line */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-900">TDS Details</h3>
                            {!isAuditor && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) && (
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handleTdsEditClick}
                                    className="px-3 py-1 text-xs"
                                >
                                    <I.Edit className="w-3 h-3 mr-1" />
                                    Edit TDS
                                </Button>
                            )}
                        </div>
                        <div className="rounded-lg border bg-white p-4 text-sm space-y-2">
                            {invoice.tdsAmount != null || invoice.tdsPercentage != null || invoice.tdsRule ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Amount</p>
                                        <p className="font-medium text-gray-900">{formatCurrency(invoice.tdsAmount)}</p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Percentage</p>
                                        <p className="font-medium text-gray-900">
                                            {invoice.tdsPercentage != null ? `${invoice.tdsPercentage}%` : 'N/A'}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Rule</p>
                                        <p className="font-medium text-gray-900">{getTdsRuleDescription(invoice.tdsRule) || 'N/A'}</p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Batch</p>
                                        <p className="font-medium text-gray-900">{invoice.tdsBatchNumber || 'Batch not generated'}</p>
                                    </div>
                                    <div className="border-t my-2" />
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-900 font-semibold">TDS Status</p>
                                        <p className="text-lg font-bold text-gray-900">
                                            {String(invoice.status || '').toUpperCase() === 'TDS_PAID'
                                                ? 'TDS Paid'
                                                : 'TDS not paid'}
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <p className="text-gray-500">No TDS data available for this invoice.</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-900">Invoice Summary</h3>
                            {!isAuditor && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) && (
                                <Button
                                    size="xs"
                                    variant={isEditingAmounts ? "outline" : "primary"}
                                    onClick={isEditingAmounts ? handleAmountUpdate : handleAmountEdit}
                                    disabled={savingAmounts}
                                    className="px-3 py-1 text-xs"
                                >
                                    {savingAmounts ? (
                                        <>
                                            <I.Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                            Updating...
                                        </>
                                    ) : isEditingAmounts ? (
                                        <>
                                            <I.Check className="w-3 h-3 mr-1" />
                                            Update
                                        </>
                                    ) : (
                                        <>
                                            <I.Edit className="w-3 h-3 mr-1" />
                                            Edit Amounts
                                        </>
                                    )}
                                </Button>
                            )}
                            {isEditingAmounts && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) && (
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handleAmountCancel}
                                    disabled={savingAmounts}
                                    className="px-3 py-1 text-xs ml-2"
                                >
                                    <I.X className="w-3 h-3 mr-1" />
                                    Cancel
                                </Button>
                            )}
                        </div>
                        <div className="rounded-lg border bg-white p-4 text-sm space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Subtotal</span>
                                {isEditingAmounts && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="w-32 px-2 py-1 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                        value={editingAmounts.subtotal}
                                        onChange={(e) => handleAmountChange('subtotal', e.target.value)}
                                    />
                                ) : (
                                    <span className="font-medium text-gray-900">{formatCurrency(invoice.subtotal)}</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Tax</span>
                                {isEditingAmounts && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="w-32 px-2 py-1 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                        value={editingAmounts.tax}
                                        onChange={(e) => handleAmountChange('tax', e.target.value)}
                                    />
                                ) : (
                                    <span className="font-medium text-gray-900">{formatCurrency(invoice.tax)}</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">TDS Amount</span>
                                <span className="font-medium text-gray-900">
                                    {formatCurrency(isEditingAmounts ? editingAmounts.tdsAmount : invoice.tdsAmount)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Invoice Payable</span>
                                <span className="font-semibold text-gray-900">
                                    {formatCurrency(isEditingAmounts ? editingAmounts.invoicePayable : invoice.invoicePayable)}
                                </span>
                            </div>
                            <div className="border-t my-2" />
                            <div className="flex items-center justify-between">
                                <span className="text-gray-900 font-semibold">Grand Total</span>
                                <span className="text-lg font-bold text-gray-900">
                                    {formatCurrency(isEditingAmounts ? editingAmounts.totalAmount : invoice.totalAmount)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status Console */}
                <Card className="p-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Status History</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm border rounded-lg overflow-hidden">
                            <thead className="bg-blue-600 text-white">
                                <tr>
                                    <th className="px-4 py-2 text-left">Status</th>
                                    <th className="px-4 py-2 text-left">User</th>
                                    <th className="px-4 py-2 text-left">Remarks</th>
                                    <th className="px-4 py-2 text-left">Log Time</th>
                                </tr>
                            </thead>
                            <tbody className="bg-blue-50 text-gray-900">
                                {(String(invoice.paymentNotificationStatus).toUpperCase() === 'SENT') && (
                                    <tr className="border-t">
                                        <td className="px-4 py-2">Email Notification</td>
                                        <td className="px-4 py-2">{invoice.paymentNotificationSentBy || 'N/A'}</td>
                                        <td className="px-4 py-2">
                                            <div className="flex flex-col">
                                                <span>{invoice.paymentNotificationRemarks || (String(invoice.paymentNotificationStatus).toUpperCase() === 'SENT' ? 'Mail sent to client' : 'Mail not sent')}</span>
                                                {invoice.paymentNotificationBody && (
                                                    <button
                                                        onClick={() => {
                                                            const remarks = invoice.paymentNotificationRemarks || '';
                                                            // Match "Mail sent: <email>[, CC: <emails>]"
                                                            // Regex: Starts with "Mail sent: ", captures first email until comma/end, then optionally ", CC: " and rest
                                                            const mailSentMatch = remarks.match(/^Mail sent:\s*([^,]+)(?:,\s*CC:\s*(.+))?/i);

                                                            const sentTo = mailSentMatch ? mailSentMatch[1] : '';
                                                            const cc = mailSentMatch ? mailSentMatch[2] : '';

                                                            setEmailLogModalData({
                                                                subject: invoice.paymentNotificationSubject,
                                                                body: invoice.paymentNotificationBody,
                                                                sentBy: invoice.paymentNotificationSentBy,
                                                                sentAt: invoice.paymentNotificationSentAt,
                                                                remarks: invoice.paymentNotificationRemarks,
                                                                sentTo: sentTo,
                                                                cc: cc
                                                            });
                                                            setShowEmailLogModal(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 text-xs text-left mt-1 underline"
                                                    >
                                                        View Log
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">{formatDateTime(invoice.paymentNotificationSentAt)}</td>
                                    </tr>
                                )}
                                {String(invoice.status || '').toUpperCase() === 'TDS_PAID' && (
                                    <tr className="border-t">
                                        <td className="px-4 py-2">TDS Paid</td>
                                        <td className="px-4 py-2">{tdsBatchInfo?.batchPaidBy || 'N/A'}</td>
                                        <td className="px-4 py-2">{tdsBatchInfo?.paymentRemarks || 'N/A'}</td>
                                        <td className="px-4 py-2">{formatDateTime(tdsBatchInfo?.batchPaidDate)}</td>
                                    </tr>
                                )}
                                {(invoice.invoicePaidDate || invoice.invoicePaidBy) && (
                                    <tr className="border-t">
                                        <td className="px-4 py-2">Invoice Paid</td>
                                        <td className="px-4 py-2">{invoice.invoicePaidBy || 'N/A'}</td>
                                        <td className="px-4 py-2">{invoice.invoicePaymentMethod || ''}</td>
                                        <td className="px-4 py-2">{formatDateTime(invoice.invoicePaidDate)}</td>
                                    </tr>
                                )}
                                {(invoice.invoiceApprovedBy || invoice.invoiceApprovedTime) && (
                                    <tr className="border-t">
                                        <td className="px-4 py-2">Approved</td>
                                        <td className="px-4 py-2">{invoice.invoiceApprovedBy || 'N/A'}</td>
                                        <td className="px-4 py-2">Invoice approved</td>
                                        <td className="px-4 py-2">{formatDateTime(invoice.invoiceApprovedTime)}</td>
                                    </tr>
                                )}
                                <tr className="border-t">
                                    <td className="px-4 py-2">Pending Approval</td>
                                    <td className="px-4 py-2">System</td>
                                    <td className="px-4 py-2">System Created</td>
                                    <td className="px-4 py-2">{formatDateTime(invoice.dateReceived)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card>
            </Card>

            {/* TDS Edit Modal */}
            <Modal
                isOpen={isTdsEditModalOpen}
                onClose={() => !updatingTds && setIsTdsEditModalOpen(false)}
                title="Update TDS Percentage"
            >
                <form onSubmit={handleTdsUpdate} className="space-y-4">
                    {invoice && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Invoice #{invoice.invoiceNumber}
                                </label>
                                <p className="text-sm text-gray-500 mb-4">
                                    Vendor: {vendorName}
                                </p>
                            </div>

                            <div>
                                <label htmlFor="tdsRule" className="block text-sm font-medium text-gray-700 mb-1">
                                    TDS Section
                                </label>
                                <div className="w-full">
                                    <Select
                                        id="tdsRule"
                                        value={tdsFormData.tdsRuleId}
                                        onChange={(e) => {
                                            const ruleId = e.target.value;
                                            const rule = tdsRules.find(r => r.id.toString() === ruleId.toString());
                                            setTdsFormData(prev => ({
                                                ...prev,
                                                tdsRuleId: ruleId,
                                                tdsPercentage: rule?.percentage || 0
                                            }));
                                        }}
                                        options={tdsRuleOptions}
                                        disabled={loadingTdsRules || updatingTds}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 border-t flex justify-end space-x-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsTdsEditModalOpen(false)}
                                    disabled={updatingTds}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={updatingTds || !tdsFormData.tdsRuleId}
                                    className="min-w-[100px]"
                                >
                                    {updatingTds ? (
                                        <>
                                            <I.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <I.Check className="w-4 h-4 mr-2" />
                                            Update TDS
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            </Modal>

            {/* Inline Document modal - handles both PDFs and images */}
            {showPdf && pdfUrl && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                    <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[85vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 border-b">
                            <span className="text-sm font-medium text-gray-700">Invoice Document</span>
                            <button
                                type="button"
                                onClick={() => setShowPdf(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <I.X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1">
                            {pdfUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                                // Display image in iframe
                                <iframe
                                    src={pdfUrl}
                                    title="Invoice Document"
                                    className="w-full h-full border-0"
                                    frameBorder="0"
                                    onLoad={() => console.log('[INVOICE-DETAIL] Image iframe loaded')}
                                    onError={(e) => console.error('[INVOICE-DETAIL] Image iframe error:', e)}
                                />
                            ) : (
                                // Display PDF with viewer parameters
                                <iframe
                                    src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                                    title="Invoice Document"
                                    className="w-full h-full border-0"
                                    frameBorder="0"
                                    onLoad={() => console.log('[INVOICE-DETAIL] PDF iframe loaded')}
                                    onError={(e) => console.error('[INVOICE-DETAIL] PDF iframe error:', e)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Email Log Modal */}
            {showEmailLogModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-3/4 max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-xl font-bold text-gray-800">Email Log Details</h3>
                            <button
                                onClick={() => setShowEmailLogModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto">
                            {/* Metadata Card */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-2">
                                <div className="grid grid-cols-[100px_1fr] gap-2">
                                    <span className="font-semibold text-gray-500">Subject:</span>
                                    <span className="text-gray-900 font-medium">{emailLogModalData.subject || 'N/A'}</span>
                                </div>
                                <div className="grid grid-cols-[100px_1fr] gap-2">
                                    <span className="font-semibold text-gray-500">Sent By:</span>
                                    <span className="text-gray-900">{emailLogModalData.sentBy || 'N/A'}</span>
                                </div>
                                <div className="grid grid-cols-[100px_1fr] gap-2">
                                    <span className="font-semibold text-gray-500">Sent At:</span>
                                    <span className="text-gray-900">{formatDateTime(emailLogModalData.sentAt)}</span>
                                </div>
                                {emailLogModalData.sentTo && (
                                    <div className="grid grid-cols-[100px_1fr] gap-2">
                                        <span className="font-semibold text-gray-500">Sent To:</span>
                                        <span className="text-gray-900">{emailLogModalData.sentTo}</span>
                                    </div>
                                )}
                                {emailLogModalData.cc && (
                                    <div className="grid grid-cols-[100px_1fr] gap-2">
                                        <span className="font-semibold text-gray-500">CC:</span>
                                        <span className="text-gray-900">{emailLogModalData.cc}</span>
                                    </div>
                                )}
                                {!emailLogModalData.sentTo && (
                                    <div className="grid grid-cols-[100px_1fr] gap-2">
                                        <span className="font-semibold text-gray-500">Remarks:</span>
                                        <span className="text-gray-900">{emailLogModalData.remarks || 'N/A'}</span>
                                    </div>
                                )}
                            </div>

                            {/* Body Card */}
                            <div className="bg-white border border-gray-200 rounded-lg p-6 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed shadow-sm min-h-[200px]">
                                <div dangerouslySetInnerHTML={{ __html: emailLogModalData.body || '<p class="text-gray-500 italic">No content available.</p>' }} />
                            </div>
                        </div>

                        <div className="p-4 border-t flex justify-end bg-gray-50 rounded-b-lg">
                            <button
                                onClick={() => setShowEmailLogModal(false)}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 shadow-sm transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceDetailPage;
